import { TrendingUp, TrendingDown } from "lucide-react";
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { ChartContainer } from "../ui/chart";
import { toFiniteNumber } from "src/lib/utils";

export const description = "Temperature";

const chartConfig = {
  visitors: {
    label: "Visitors",
  },
  safari: {
    label: "Safari",
    color: "hsl(var(--chart-5))",
  },
};

export default function TemperatureDisplay(props) {
  // null when the selected period has no valid samples
  const temperature = toFiniteNumber(props.temperature);
  const trend = toFiniteNumber(props.tempTrend);
  const hasData = temperature !== null;
  const chartData = [
    { browser: "safari", visitors: temperature ?? 0, fill: "var(--color-safari)" },
  ];

  return (
    <Card className="flex flex-col min-h-[300px] min-w-[250px]">
      <CardHeader className="flex flex-col items-center pb-0 text-center">
        <CardTitle className="whitespace-normal break-words">Gateway Temperature</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square max-h-[250px] min-h-[200px]"
        >
          <RadialBarChart
            data={chartData}
            startAngle={0}
            endAngle={108}
            innerRadius={80}
            outerRadius={110}
          >
            <PolarGrid
              gridType="circle"
              radialLines={false}
              stroke="none"
              className="first:fill-muted last:fill-background"
              polarRadius={[86, 74]}
            />
            <RadialBar dataKey="visitors" background cornerRadius={10} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className={hasData ? "fill-foreground text-4xl font-bold" : "fill-muted-foreground text-xl font-semibold"}
                        >
                          {hasData ? temperature.toLocaleString() : "No data"}
                        </tspan>
                        {hasData && (
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-muted-foreground"
                          >
                            Degrees
                          </tspan>
                        )}
                      </text>
                    );
                  }
                }}
              />
            </PolarRadiusAxis>
          </RadialBarChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="flex items-center gap-2 font-medium leading-none">
          {hasData && trend !== null && (
            <div className="flex items-center gap-1">
              <span>
                Trending {trend >= 0 ? "up by " : "down by "}
                {Math.abs(trend)}% last week
              </span>
              {trend >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
            </div>
          )}
        </div>
        {!hasData && (
          <div className="leading-none text-muted-foreground">No temperature readings in the selected period</div>
        )}
      </CardFooter>
    </Card>
  );
};